import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useAcoesVisita } from "../lib/acoesVisita";
import { buscarVisitas } from "../lib/visitas";
import { Cabecalho } from "../components/Layout";
import { BotaoNovaVisita } from "../components/BotaoNovaVisita";
import { TabelaVisitas } from "../components/TabelaVisitas";
import { ROTULO_STATUS, type StatusVisita, type Visita } from "../lib/tipos";

export default function Visitas() {
  const { podeAoMenos } = useAuth();
  const podeEditar = podeAoMenos("CONSULTOR");

  const [itens, setItens] = useState<Visita[]>([]);
  const [total, setTotal] = useState(0);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");

  /**
   * O status pode vir na URL (`/visitas?status=EM_ANDAMENTO`) — é assim que os
   * cartões do dashboard trazem a lista já filtrada. Depois disso o filtro é
   * comum: mexer nele não mexe mais no endereço.
   */
  const [parametros] = useSearchParams();
  const [busca, setBusca] = useState("");
  const [status, setStatus] = useState<StatusVisita | "">(
    () => (parametros.get("status") as StatusVisita | null) ?? "",
  );
  const [de, setDe] = useState("");
  const [ate, setAte] = useState("");

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await buscarVisitas({
        busca,
        status,
        // o input date dá só a data; completo com o início e o fim do dia
        de: de ? `${de}T00:00:00` : undefined,
        ate: ate ? `${ate}T23:59:59` : undefined,
        porPagina: 500,
      });
      setItens(r.itens);
      setTotal(r.total);
      setErro("");
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Falha ao carregar as visitas");
    } finally {
      setCarregando(false);
    }
  }, [busca, status, de, ate]);

  useEffect(() => {
    const t = setTimeout(carregar, 300);
    return () => clearTimeout(t);
  }, [carregar]);

  const acoes = useAcoesVisita(carregar);
  const temFiltro = busca || status || de || ate;

  return (
    <>
      <Cabecalho titulo="Todas as Visitas" />
      {podeEditar && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 20 }}>
          <BotaoNovaVisita onClick={acoes.abrirNova} />
        </div>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 18, alignItems: "center" }}>
        <input
          className="campo"
          style={{ flex: "1 1 220px", maxWidth: 320 }}
          placeholder="🔍 Pesquisar por aluno, responsável ou telefone"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        <select className="campo" style={{ width: 190 }} value={status} onChange={(e) => setStatus(e.target.value as StatusVisita | "")}>
          <option value="">Todos os Status</option>
          {Object.entries(ROTULO_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 12, color: "var(--texto-suave)", fontWeight: 500 }}>De:</span>
          <input type="date" className="campo" style={{ width: 150 }} value={de} onChange={(e) => setDe(e.target.value)} />
          <span style={{ fontSize: 12, color: "var(--texto-suave)", fontWeight: 500 }}>Até:</span>
          <input type="date" className="campo" style={{ width: 150 }} value={ate} onChange={(e) => setAte(e.target.value)} />
        </div>
        {temFiltro && (
          <button className="botao secundario" onClick={() => { setBusca(""); setStatus(""); setDe(""); setAte(""); }}>
            Limpar
          </button>
        )}
        <div style={{ marginLeft: "auto", fontSize: 13, color: "var(--texto-suave)" }}>
          {total} visita(s)
        </div>
      </div>

      {erro && <div className="erro" style={{ marginBottom: 12 }}>{erro}</div>}

      <TabelaVisitas
        visitas={itens}
        carregando={carregando}
        colunas={["responsavel", "aluno", "telefone", "curso", "serie", "inicio", "status", "valor"]}
        podeEditar={podeEditar}
        onEditar={acoes.abrirEdicao}
        onFicha={acoes.abrirFicha}
        onExcluir={acoes.excluir}
        onWhatsapp={acoes.enviarWhatsapp}
      />

      {acoes.modais}
    </>
  );
}
